import React from 'react';
import { useTranslation } from 'react-i18next';
import { HStack, Pressable, Text } from '@gluestack-ui/themed';

export type ScoreProgressionAxisMode = 'attempt' | 'sessionWeek';

export type XAxisToggleProps = {
  value: ScoreProgressionAxisMode;
  onChange: (value: ScoreProgressionAxisMode) => void;
  disabled?: boolean;
};

/**
 * Switches score progression between attempt number and session week (no refetch).
 */
export function XAxisToggle({ value, onChange, disabled = false }: XAxisToggleProps) {
  const { t } = useTranslation();

  const options: { label: string; value: ScoreProgressionAxisMode; testID: string }[] = [
    {
      label: t('admin.analytics.scoreProgression.axisAttempt'),
      value: 'attempt',
      testID: 'score-progression-axis-attempt',
    },
    {
      label: t('admin.analytics.scoreProgression.axisSessionWeek'),
      value: 'sessionWeek',
      testID: 'score-progression-axis-session-week',
    },
  ];

  return (
    <HStack
      testID="score-progression-axis-toggle"
      bg="$bgSubtle"
      height={28}
      p={2}
      borderRadius="$md"
      alignSelf="flex-start"
      borderWidth={1}
      borderColor="$borderLight"
      alignItems="center"
    >
      {options.map((option) => {
        const isActive = value === option.value;
        return (
          <Pressable
            key={option.value}
            onPress={() => {
              if (!disabled) onChange(option.value);
            }}
            height={24}
            px="$2"
            borderRadius="$sm"
            bg={isActive ? '$white' : 'transparent'}
            opacity={disabled ? 0.6 : 1}
            alignItems="center"
            justifyContent="center"
            testID={option.testID}
            accessibilityRole="button"
            accessibilityState={{ selected: isActive, disabled }}
          >
            <Text
              size="xs"
              color={isActive ? '$textHeading' : '$textSubtle'}
              fontFamily={isActive ? '$heading' : undefined}
              numberOfLines={1}
            >
              {option.label}
            </Text>
          </Pressable>
        );
      })}
    </HStack>
  );
}
